import { config } from '../config.ts';
import type { Mat4 } from '../util/mat.ts';
import { mat4LookAt, mat4Multiply, mat4Perspective } from '../util/mat.ts';
import type { Vec3 } from '../util/vec.ts';
import { v3cross, v3normalize } from '../util/vec.ts';

export interface FreeFlyInput {
  forward: number;   // -1..1   (W = +1, S = -1)
  strafe: number;    // -1..1   (D = +1, A = -1)
  vertical: number;  // -1..1   (E = +1, Q = -1)
  mouseDX: number;   // pixels accumulated this frame
  mouseDY: number;
  fast: boolean;     // shift held
}

export interface FreeFlyCamera {
  update(dt: number, aspect: number, input: FreeFlyInput): void;
  setPose(eye: Vec3, yaw: number, pitch: number): void;
  /** Multiplier on the vertical FOV; < 1 zooms in. Matches the choreographer's
   *  `fovScale` semantics. Clamped to [zoomFovMin, zoomFovMax]. */
  setFovScale(scale: number): void;
  readonly viewProj: Mat4;
  readonly eye: Vec3;
}

export interface CreateOpts {
  heightSampler?: (worldX: number, worldZ: number) => number;
}

const DEG2RAD = Math.PI / 180;
const FOV_Y_RAD = config.fovYDeg * DEG2RAD;
const UP_Y: Vec3 = [0, 1, 0];
const WORLD_HALF = config.worldSize * 0.5;

// Smooth maximum: rounds the join where pos.y would otherwise kink against terrain.
function softMax(a: number, b: number, k: number): number {
  const diff = a - b;
  return 0.5 * (a + b + Math.sqrt(diff * diff + k * k));
}

const clamp = (x: number, lo: number, hi: number): number =>
  x < lo ? lo : x > hi ? hi : x;

export function createFreeFlyCamera(opts: CreateOpts = {}): FreeFlyCamera {
  const heightSampler = opts.heightSampler;
  let px = 0, py = 0, pz = 0;
  let yaw = 0;
  let pitch = 0;
  let fovScale = 1;
  let viewProj: Mat4 = new Float32Array(16);

  const update = (dt: number, aspect: number, input: FreeFlyInput): void => {
    yaw += input.mouseDX * config.mouseSens;
    pitch -= input.mouseDY * config.mouseSens;
    if (pitch > config.flyPitchLimit) pitch = config.flyPitchLimit;
    if (pitch < -config.flyPitchLimit) pitch = -config.flyPitchLimit;

    const cp = Math.cos(pitch);
    const sp = Math.sin(pitch);
    const sy = Math.sin(yaw);
    const cy = Math.cos(yaw);
    // yaw=0 looks down -Z (matches mat4LookAt's default forward).
    const dir: Vec3 = [cp * sy, sp, -cp * cy];
    const right = v3normalize(v3cross(dir, UP_Y));

    const speed = config.flyMoveSpeed * (input.fast ? config.flyFastMultiplier : 1);
    const step = speed * dt;

    px += (dir[0] * input.forward + right[0] * input.strafe) * step;
    py += (dir[1] * input.forward + input.vertical) * step;
    pz += (dir[2] * input.forward + right[2] * input.strafe) * step;

    // Soft-clamp eye height to terrain + clearance so the user can't fly through the ground.
    if (heightSampler) {
      const cx = clamp(px, -WORLD_HALF, WORLD_HALF);
      const cz = clamp(pz, -WORLD_HALF, WORLD_HALF);
      const ground = heightSampler(cx, cz);
      py = softMax(py, ground + config.flyEyeClearance, 4);
    }

    const eye: Vec3 = [px, py, pz];
    const target: Vec3 = [px + dir[0], py + dir[1], pz + dir[2]];
    const view = mat4LookAt(eye, target, UP_Y);
    const proj = mat4Perspective(FOV_Y_RAD * fovScale, aspect, config.near, config.far);
    viewProj = mat4Multiply(proj, view);
  };

  const setPose = (eye: Vec3, y: number, p: number): void => {
    px = eye[0]; py = eye[1]; pz = eye[2];
    yaw = y;
    pitch = clamp(p, -config.flyPitchLimit, config.flyPitchLimit);
  };

  const setFovScale = (scale: number): void => {
    fovScale = clamp(scale, config.zoomFovMin, config.zoomFovMax);
  };

  return {
    update,
    setPose,
    setFovScale,
    get viewProj(): Mat4 {
      return viewProj;
    },
    get eye(): Vec3 {
      return [px, py, pz];
    },
  };
}
